import { ROLE_APP } from "constants/actionAccess";
import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import jwt_decode from "jwt-decode";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import userProfileService from "services/userProfileService";
import { AUTH_TOKEN } from "store/auth/login/actionTypes";

// Detail Runner User
export const useDetailRunnerUser = (payload, onSuccess, onError) => {
  return useQuery(
    ["runner-detail"],
    () => userProfileService.detailRunnerUser(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// List Runner User
export const useListRunnerUser = (onSuccess, onError) => {
  return useQuery(["runner-list"], () => userProfileService.listRunnerUser(), {
    enabled: false,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    select: response => {
      const { data } = response;
      return data;
    },
  });
};

// List Runner User
export const useListCollectorUser = (onSuccess, onError) => {
  return useQuery(
    ["collector-list"],
    () => userProfileService.listCollectorUser(),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// List Role User Profile
export const useListRoleUserProfile = (payload = {}, onSuccess, onError) => {
  const { t } = useTranslation(["userProfile"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["user-profile-roles", params?.page || "all"],
    () => userProfileService.listRoleUserProfile(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("role"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data || [],
          params: params,
          totalCount: data?.length > 0 ? data.length : 0,
        };
      },
    }
  );
};

// List Email Domain User Profile
export const useListEmailDomainUserProfile = (
  payload = {},
  onSuccess,
  onError
) => {
  const { t } = useTranslation(["userProfile"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["user-profiles-email-domains", params?.page || "all"],
    () => userProfileService.listEmailDomainUserProfile(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("email_domain"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data?.length > 0 ? data.length : 0,
        };
      },
    }
  );
};

// List User Profile
export const useListUserProfile = (payload, onSuccess, onError, key) => {
  const { t } = useTranslation(["userProfile"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    [key || "user-profiles", params],
    () => userProfileService.listUserProfile(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("user_profile"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
          meta: data._meta,
        };
      },
    }
  );
};

// Create User Profile
export const useCreateUserProfile = (onSuccess, onError) => {
  const { t } = useTranslation(["userProfile"]);
  const { addToast } = useToasts();

  return useMutation(userProfileService.createUserProfile, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.create", {
          target: t("user_profile"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("user_profile"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Detail User Profile
export const useDetailUserProfile = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["userProfile"]);
  const { addToast } = useToasts();
  return useQuery(
    ["user-profile", "detail"],
    () => userProfileService.detailUserProfile(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.detail", {
                target: t("user_profile"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// Update User Profile
export const useUpdateUserProfile = (onSuccess, onError) => {
  const { t } = useTranslation(["userProfile"]);
  const { addToast } = useToasts();

  return useMutation(userProfileService.updateUserProfile, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.update", {
          target: t("user_profile"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("user_profile"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Active Multiple User
export const useActiveUserProfile = (onSuccess, onError) => {
  const { t } = useTranslation(["userProfile"]);
  const { addToast } = useToasts();

  return useMutation(userProfileService.updateUserProfile, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.active", {
          target: t("user_profile"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.active", {
              target: t("user_profile"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Delete Multiple User
export const useInactiveUserProfile = (onSuccess, onError) => {
  const { t } = useTranslation(["userProfile"]);
  const { addToast } = useToasts();

  return useMutation(userProfileService.updateUserProfile, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.delete", {
          target: t("user_profile"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.delete", {
              target: t("user_profile"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// useMyHubsList
export const useMyHubsList = (payload = {}, onSuccess, onError) => {
  const { t } = useTranslation(["hub"]);
  const { addToast } = useToasts();

  const { params, enabled, queryKey } = payload;

  return useQuery(
    [queryKey || "myHubsList"],
    () => userProfileService.myHubsList(params),
    {
      // staleTime: 5 * 60 * 1000, // 5 minutes
      // cacheTime: 5 * 60 * 1000, // 5 minutes
      enabled: enabled || false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("myHubsList"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data, result } = response?.data;
        if (result) {
          const { hub, is_hubadmin, parent_hub, childs_hub, user } = data;
          return {
            hub,
            is_hubadmin,
            parent_hub,
            childs_hub: childs_hub || [],
            user,
          };
        } else {
          return {
            hub: null,
            is_hubadmin: false,
            parent_hub: null,
            childs_hub: [],
            user: null,
          };
        }
      },
    }
  );
};

// List logadmin-not-manage-center
export const useListLogAdminNotManageCenter = (
  payload = {},
  onSuccess,
  onError
) => {
  const params = payload?.params || {};
  return useQuery(
    ["list-logadmin-not-manage-center", params],
    () => userProfileService.listLogAdminNotManageCenter(params),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") onError(errorResponse);
      },
    }
  );
};

export const useListHubAdminNotManageHub = (
  payload = {},
  onSuccess,
  onError
) => {
  const params = payload?.params || {};
  return useQuery(
    ["list-hubadmin-not-manage-hub", params],
    () => userProfileService.listHubAdminNotManageHub(params),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") onError(errorResponse);
      },
    }
  );
};

export const useListRunnerIsFree = (payload = {}, onSuccess, onError) => {
  const params = payload?.params || {};
  return useQuery(
    ["list-runner-is-free", params],
    () => userProfileService.listRunnerIsFree(params),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") onError(errorResponse);
      },
    }
  );
};

export const useListCollectorIsFree = (payload = {}, onSuccess, onError) => {
  const params = payload?.params || {};
  return useQuery(
    ["list-collector-is-free", params],
    () => userProfileService.listCollectorIsFree(params),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") onError(errorResponse);
      },
    }
  );
};

export const useListModelDependHub = (payload, onSuccess, onError) => {
  const { queryKey, enabled, params } = payload;
  return useQuery(
    [queryKey || "list-model-depend-hub"],
    () => userProfileService.getModelDependHub(payload),
    {
      enabled: enabled,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => {
        const { items } = response?.data;
        return {
          items: items || [],
          params: params,
        };
      },
    }
  );
};

export const useCheckRole = () => {
  const info = jwt_decode(localStorage.getItem(AUTH_TOKEN));
  const listRole = info["auth"].split(",");

  const checkRole = (listRole, role) => {
    const roleList = Array.isArray(role) ? role : [role];
    return roleList.some(roleItem =>
      listRole.some(item => item.includes(roleItem))
    );
  };

  const isHubAdmin = useMemo(() => {
    return checkRole(listRole, ROLE_APP.ROLE_HUB_ADMIN);
  }, [listRole]);

  const isLogAdmin = useMemo(() => {
    return checkRole(listRole, [
      ROLE_APP.ROLE_LOG_ADMIN,
      ...(ROLE_APP.ROLE_LOG_ADMIN_ALIASES || []),
    ]);
  }, [listRole]);

  return {
    isHubAdmin,
    isLogAdmin,
  };
};

// List user-type
export const useListUserType = (onSuccess, onError) => {
  return useQuery(["list-user-type"], () => userProfileService.listUserType(), {
    enabled: false,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    select: res => res?.data,
  });
};

// List user-type
export const useListDepartment = (payload, onSuccess, onError) => {
  return useQuery(
    ["list-department"],
    () => userProfileService.listDepartment(payload?.params),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: res => res?.data,
    }
  );
};
